// Command registry for the spotlight command menu.
// Entry shape: { id, labelKey: i18n key, keywords: [lowercase], run: (dispatch, ctx) => void }
// ctx: { diagramId, dbEngine }. labelKey is plain data — resolving it (t(labelKey))
// happens in the rendering component (DiagramSearch.jsx), which also feeds the
// resolved string into search matching so it matches in the user's language.
import {
    toggleSupport,
} from '../../redux/main/main.actions';
import {
    enableBtnNewTableClick,
    enableBtnNewNoteClick,
    toggleBtnNewLine,
    toggleBtnNewArea,
    toggleBtnExportImage,
    toggleModalDiagramExportSQL,
    toggleBtnGridStyle,
    toggleControlsFocusMode,
    toggleBtnMinimap,
    toggleBtnModalShortcuts,
} from '../../redux/diagram/diagram.actions';

const SQL_ENGINES = [
    ['mysql', 'MySQL'],
    ['postgresql', 'PostgreSQL'],
    ['sqlserver', 'SQL Server'],
    ['oracle', 'Oracle'],
    ['mariadb', 'MariaDB'],
];

const GRID_STYLES = [
    [null, 'none', 'dropdown_ctrl_grid.none'],
    ['lines', 'lines', 'dropdown_ctrl_grid.lines'],
    ['dots', 'dots', 'dropdown_ctrl_grid.dots'],
];

export const SPOTLIGHT_COMMANDS = [
    {
        id: 'new-table',
        section: 'canvas',
        labelKey: 'search_menu.cmd_new_table',
        icon: <svg className="command-icon-plus" width="20px" height="20px" viewBox="0 0 24 24" strokeWidth="0.2px" stroke="black"><path d="M12,18c-0.6,0-1-0.4-1-1V7c0-0.6,0.4-1,1-1s1,0.4,1,1v10C13,17.6,12.6,18,12,18z" fill="rgb(134, 145, 148)"></path><path d="M17,13H7c-0.6,0-1-0.4-1-1s0.4-1,1-1h10c0.6,0,1,0.4,1,1S17.6,13,17,13z" fill="rgb(134, 145, 148)"></path></svg>,
        keywords: ['new', 'create', 'table', 'add'],
        run: (dispatch) => dispatch(enableBtnNewTableClick()),
    },
    {
        id: 'new-line',
        section: 'canvas',
        labelKey: 'search_menu.cmd_new_line',
        icon: <svg className="command-icon-plus" width="20px" height="20px" viewBox="0 0 24 24" strokeWidth="0.2px" stroke="black"><path d="M12,18c-0.6,0-1-0.4-1-1V7c0-0.6,0.4-1,1-1s1,0.4,1,1v10C13,17.6,12.6,18,12,18z" fill="rgb(134, 145, 148)"></path><path d="M17,13H7c-0.6,0-1-0.4-1-1s0.4-1,1-1h10c0.6,0,1,0.4,1,1S17.6,13,17,13z" fill="rgb(134, 145, 148)"></path></svg>,
        keywords: ['new', 'create', 'line', 'relation', 'reference'],
        run: (dispatch) => dispatch(toggleBtnNewLine()),
    },
    {
        id: 'new-area',
        section: 'canvas',
        labelKey: 'search_menu.cmd_new_area',
        icon: <svg className="command-icon-plus" width="20px" height="20px" viewBox="0 0 24 24" strokeWidth="0.2px" stroke="black"><path d="M12,18c-0.6,0-1-0.4-1-1V7c0-0.6,0.4-1,1-1s1,0.4,1,1v10C13,17.6,12.6,18,12,18z" fill="rgb(134, 145, 148)"></path><path d="M17,13H7c-0.6,0-1-0.4-1-1s0.4-1,1-1h10c0.6,0,1,0.4,1,1S17.6,13,17,13z" fill="rgb(134, 145, 148)"></path></svg>,
        keywords: ['new', 'create', 'area', 'group'],
        run: (dispatch) => dispatch(toggleBtnNewArea()),
    },
    {
        id: 'new-note',
        section: 'canvas',
        labelKey: 'search_menu.cmd_new_note',
        icon: <svg width="19px" height="20px" viewBox="0 0 24 24" fill="none" style={{ marginLeft: '-3px', marginRight: '6px', minWidth: '19px' }}><path d="M5 4h14a1 1 0 0 1 1 1v9l-6 6H5a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" stroke="#e1e1e1" strokeWidth="1.9" strokeLinejoin="round"></path><path d="M14 20v-5a1 1 0 0 1 1-1h5" stroke="#e1e1e1" strokeWidth="1.9" strokeLinejoin="round"></path></svg>,
        keywords: ['new', 'create', 'note', 'sticky', 'add'],
        run: (dispatch) => dispatch(enableBtnNewNoteClick()),
    },

    // ...SQL_ENGINES.map(([engine, name]) => ({
    //     id: `export-sql-${engine}`,
    //     section: 'export',
    //     label: (t) => `${t('dropdown_ctrl_export.export_code')} · ${name}`,
    //     keywords: ['export', 'sql', 'code', engine, name.toLowerCase()],
    //     run: (dispatch) => dispatch(toggleModalDiagramExportSQL(engine)),
    // })),
    // ...GRID_STYLES.map(([value, word, labelKey]) => ({
    //     id: `grid-${word}`,
    //     section: 'preferences',
    //     label: (t) => `${t('t.ctrl_top_left.grid')} · ${t(labelKey)}`,
    //     keywords: ['grid', 'background', word],
    //     run: (dispatch) => dispatch(setGridStyle(value)),
    // })),
    {
        id: 'export-image',
        section: 'export',
        labelKey: 'search_menu.cmd_export_image',
        keywords: ['export', 'image', 'png', 'picture', 'screenshot'],
        run: (dispatch) => dispatch(toggleBtnExportImage()),
    },
    {
        id: 'export-sql',
        section: 'export',
        labelKey: 'search_menu.cmd_export_sql',
        keywords: ['export', 'sql', 'code', 'ddl', 'database'],
        run: (dispatch, ctx) => {
            const supported = ['mysql', 'postgresql', 'sqlserver', 'oracle', 'mariadb'];
            dispatch(toggleModalDiagramExportSQL(supported.includes(ctx.dbEngine) ? ctx.dbEngine : 'mysql'));
        },
    },
    {
        id: 'grid-style',
        section: 'preferences',
        labelKey: 'search_menu.cmd_grid_style',
        keywords: ['grid', 'layout', 'background', 'lines', 'dots', 'change'],
        run: (dispatch) => dispatch(toggleBtnGridStyle()),
    },
    // {
    //     id: 'focus-mode',
    //     section: 'preferences',
    //     label: (t) => t('t.ctrl_bottom_right.full_screen'),
    //     keywords: ['focus', 'fullscreen', 'hide', 'controls'],
    //     run: (dispatch) => dispatch(toggleControlsFocusMode()),
    // },
    // {
    //     id: 'minimap',
    //     section: 'preferences',
    //     label: (t) => t('t.ctrl_bottom_right.minimap'),
    //     keywords: ['minimap', 'map', 'overview'],
    //     run: (dispatch) => dispatch(toggleBtnMinimap()),
    // },
    // {
    //     id: 'keyboard-shortcuts',
    //     section: 'settings',
    //     label: (t) => t('dropdown_ctrl_settings.keyboard_shortcuts'),
    //     keywords: ['keyboard', 'shortcuts', 'help', 'hotkeys'],
    //     run: (dispatch) => dispatch(toggleBtnModalShortcuts()),
    // },
    // {
    //     id: 'send-feedback',
    //     section: 'settings',
    //     label: (t) => t('t.ctrl_top_right.feedback'),
    //     keywords: ['feedback', 'support', 'contact', 'help'],
    //     run: (dispatch) => dispatch(toggleSupport('feedback', 'canvas')),
    // },
];
